import React, { useState, useEffect, useRef, memo } from "react";
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  Button,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  CircularProgress,
  useMediaQuery,
  useTheme,
  Drawer,
  Tooltip,
  Card,
  CardContent,
  Tabs,
  Tab,
  Grid,
} from "@mui/material";
import { 
  Add as AddIcon, 
  Delete as DeleteIcon, 
  Send as SendIcon,
  Edit as EditIcon,
  Menu as MenuIcon,
  ArrowBack as ArrowBackIcon,
  Chat as ChatIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Person as PersonIcon,
  ContentCopy as ContentCopyIcon,
  Check as CheckIcon,
  Folder as FolderIcon,
  CreateNewFolder as CreateNewFolderIcon,
} from "@mui/icons-material";
import generalApi from "../api/generalApi";
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { tomorrow } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { formatDistanceToNow, format } from 'date-fns';
import { useLimits } from "../context/LimitsContext";
import bots from "../const/bots.json";
import { useParams, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { languages } from '../const/languages';
import AddToProjectModal from './Project/AddToProjectModal';
import MobileAddToProjectModal from './Project/MobileAddToProjectModal';

// Import character images
import character1 from "../assets/characters/1.png";
import character2 from "../assets/characters/2.png";
import character3 from "../assets/characters/3.png";
import character4 from "../assets/characters/4.png";
import character5 from "../assets/characters/5.png";
import character6 from "../assets/characters/6.png";
import character7 from "../assets/characters/7.png";
import character8 from "../assets/characters/8.png";
import character9 from "../assets/characters/9.png";
import character10 from "../assets/characters/10.png";
import character11 from "../assets/characters/11.png";
import character12 from "../assets/characters/12.png";
import character13 from "../assets/characters/13.png";
import character14 from "../assets/characters/14.png";
import character15 from "../assets/characters/15.png";
import character16 from "../assets/characters/16.png";
import character17 from "../assets/characters/17.png";
import character18 from "../assets/characters/18.png";

// Map of available character images
export const characterImages = {
  1: character1,
  2: character2,
  3: character3,
  4: character4,
  5: character5,
  6: character6,
  7: character7,
  8: character8,
  9: character9,
  10: character10,
  11: character11,
  12: character12,
  13: character13,
  14: character14,
  15: character15,
  16: character16,
  17: character17,
  18: character18,
};

const TimeAgo = ({ timestamp, sx = {} }) => {
  const date = new Date(timestamp * 1000);
  const relativeTime = formatDistanceToNow(date, { addSuffix: true });
  const fullDate = format(date, 'PPpp');

  return (
    <Typography
      variant="caption"
      color="text.secondary"
      title={fullDate}
      sx={{ 
        cursor: 'pointer',
        ...sx
      }}
    >
      {relativeTime}
    </Typography>
  );
};

const TaskCard = ({ task, backgroundColor }) => {
  return (
    <Card
      sx={{
        mb: 2,
        mt: 1,
        backgroundColor: `${backgroundColor}05`,
        borderRadius: '12px',
        border: `1px solid ${backgroundColor}30`,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: `0 8px 28px -6px ${backgroundColor}40`,
          '& .task-description': {
            opacity: 1,
            maxHeight: '200px',
          }
        }
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '12px',
              backgroundColor: `${backgroundColor}20`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <i 
              className={task.taskIcon}
              style={{ 
                color: backgroundColor, 
                fontSize: '1.25rem',
              }} 
            />
          </Box>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
              {task.taskTitle}
            </Typography>
            <Typography 
              variant="body2" 
              color="text.secondary"
              className="task-description"
              sx={{
                opacity: 0,
                maxHeight: 0,
                transition: 'all 0.3s ease-in-out',
                overflow: 'hidden',
              }}
            >
              {task.taskDescription}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

const SidebarTaskCard = ({ task, backgroundColor, onClick }) => {
  return (
    <Card
      sx={{
        backgroundColor: `${backgroundColor}05`,
        borderRadius: '12px',
        border: `1px solid ${backgroundColor}30`,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        cursor: 'pointer',
        mt: 2,
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: `0 8px 28px -6px ${backgroundColor}40`,
          '& .task-description': {
            opacity: 1,
            maxHeight: '80px',
          }
        },
        '&:last-child': {
          mb: 3
        }
      }}
      onClick={onClick}
    >
      <CardContent sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '12px',
              backgroundColor: `${backgroundColor}20`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <i 
              className={task.taskIcon}
              style={{ 
                color: backgroundColor, 
                fontSize: '1.25rem',
              }} 
            />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography 
              variant="subtitle1" 
              sx={{ 
                fontWeight: 600,
                mb: 0.5,
                color: '#ffffff',
              }}
            >
              {task.taskTitle}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: backgroundColor,
                opacity: 0.8,
                display: 'block',
                mb: 1,
                fontSize: '0.75rem',
              }}
            >
              {task.aiCreditsToCharge} Credits
            </Typography>
            <Typography 
              variant="body2" 
              className="task-description"
              sx={{
                color: 'rgba(255, 255, 255, 0.7)',
                opacity: 0,
                maxHeight: 0,
                transition: 'all 0.3s ease-in-out',
                overflow: 'hidden',
                fontSize: '0.875rem',
                lineHeight: 1.5,
              }}
            >
              {task.taskDescription}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

const MessageInput = memo(({ onSendMessage, loading, inputRef, bot, selectedTask }) => {
  const [newMessage, setNewMessage] = useState("");

  const handleSendMessage = (e) => {
    e.preventDefault();
    e.stopPropagation();  // Prevent any parent handlers
    if (!newMessage.trim() || loading) return;  // Prevent sending if loading
    onSendMessage(newMessage);
    setNewMessage("");
    // Force focus back
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      e.stopPropagation();  // Prevent any parent handlers
      if (!loading) {  // Only send if not loading
        handleSendMessage(e);
      }
    }
  };

  return (
    <Box
      component="form"
      onSubmit={handleSendMessage}
      onClick={(e) => e.stopPropagation()}
      sx={{
        p: 2,
        pt: 4,
        pb: 3,
        mt: 3,
        bgcolor: "background.default",
        borderTop: "1px solid",
        borderColor: "divider",
        pointerEvents: 'auto',
      }}
    >
      <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
        <Box sx={{ position: 'relative', flex: 1 }}>
          <TextField
            inputRef={inputRef}
            fullWidth
            multiline
            maxRows={4}
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Type your message..."
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                bgcolor: "background.paper",
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: bot.botColor,
                  borderWidth: 2,
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: `${bot.botColor}80`,
                },
              },
              "& .MuiInputBase-input": {
                color: "text.primary",
              }
            }}
          />
          {selectedTask && (
            <Typography
              variant="caption"
              sx={{
                position: 'absolute',
                bottom: -20,
                left: 8,
                color: 'text.secondary',
                fontSize: '0.75rem',
              }}
            >
              Each message costs {selectedTask.aiCreditsToCharge} Credits
            </Typography>
          )}
        </Box>
        <Tooltip title={selectedTask ? `${selectedTask.aiCreditsToCharge} Credits per message` : "Send message"} arrow placement="top">
          <span>
            <IconButton 
              color="primary"
              onClick={handleSendMessage}
              disabled={!newMessage.trim() || loading}
              sx={{
                bgcolor: "#8B3DFF",
                color: "white",
                width: 48,
                height: 48,
                borderRadius: '50%',
                "&:hover": { bgcolor: "#7B2DEF" },
                "&:active": { bgcolor: "#6B1DDF" },
                "&.Mui-disabled": {
                  bgcolor: "action.disabledBackground",
                  color: "text.disabled"
                }
              }}
            >
              <SendIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    </Box>
  );
});

const DynamicForm = memo(({ fields, onSubmit, loading, bot, message }) => {
  const [formData, setFormData] = useState({});
  const [formValid, setFormValid] = useState(false);
  const isEditable = message?.metadata?.isEditable !== false;

  useEffect(() => {
    // Check if all required fields are filled
    const isValid = fields.every(field => {
      if (field.required) {
        return formData[field.title] && formData[field.title].trim() !== '';
      }
      return true;
    });
    setFormValid(isValid);
  }, [formData, fields]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!isEditable || !formValid || loading) return;
    onSubmit(formData);
  };

  const handleChange = (field, value) => {
    if (!isEditable) return;
    setFormData(prev => ({
      ...prev,
      [field.title]: value
    }));
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        width: '100%',
        minWidth: '400px',
        maxWidth: '800px',
        mt: 2,
        '& .MuiTextField-root': {
          bgcolor: 'background.paper',
          borderRadius: 1,
          width: '100%',
        },
        opacity: isEditable ? 1 : 0.7,
      }}
    >
      {fields.map((field, index) => (
        <TextField
          key={index}
          label={field.title}
          placeholder={field.placeholder}
          required={field.required}
          multiline={field.type === 'textArea'}
          rows={field.type === 'textArea' ? 3 : 1}
          value={formData[field.title] || ''}
          onChange={(e) => handleChange(field, e.target.value)}
          disabled={loading || !isEditable}
          error={field.required && !formData[field.title]?.trim()}
          helperText={field.required && !formData[field.title]?.trim() ? 'This field is required' : ''}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 1,
              bgcolor: 'background.default',
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: `${bot.botColor}CC`,
                borderWidth: 2,
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: `${bot.botColor}99`,
              },
            },
            '& .MuiInputLabel-root.Mui-focused': {
              color: `${bot.botColor}CC`,
            }
          }}
        />
      ))}
      {isEditable && (
        <>
          <Button
            type="submit"
            variant="contained"
            disabled={loading || !formValid}
            startIcon={loading ? <CircularProgress size={20} /> : null}
            sx={{
              mt: 1,
              bgcolor: `${bot.botColor}CC`,
              '&:hover': {
                bgcolor: `${bot.botColor}E6`,
              },
              '&:active': {
                bgcolor: `${bot.botColor}`,
              },
              '&.Mui-disabled': {
                bgcolor: 'action.disabledBackground',
                color: 'text.disabled'
              }
            }}
          >
            Submit
          </Button>
          {loading && (
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
              <LoadingDots />
            </Box>
          )}
        </>
      )}
    </Box>
  );
});

const LoadingDots = () => (
  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', height: 24 }}>
    {[0, 1, 2].map((i) => (
      <Box
        key={i}
        component="span"
        sx={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          bgcolor: 'text.secondary',
          animation: 'pulse 1s infinite',
          animationDelay: `${i * 0.2}s`,
          '@keyframes pulse': {
            '0%': { opacity: 0.3 },
            '50%': { opacity: 1 },
            '100%': { opacity: 0.3 }
          }
        }}
      />
    ))}
  </Box>
);

const ArtifactDisplay = ({ artifact, botColor, handleAddToProject }) => {
  const [activeTab, setActiveTab] = useState('visual');
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    const textToCopy = activeTab === 'visual' ? artifact : artifact;
    navigator.clipboard.writeText(textToCopy);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  
  // Updated to use the same direct approach as the test button
  const handleProjectAdd = () => {
    console.log("🚨 FOLDER ICON CLICKED - Using direct modal approach");
    
    // Instead of calling handleAddToProject, use the same direct approach as the test button
    // This ensures we're using the exact same code path that works for the test button
    try {
      // Find the MobileLayout component
      const mobileLayoutElement = document.querySelector('[data-component="MobileLayout"]');
      if (mobileLayoutElement) {
        try {
          // Create a custom event to trigger the modal directly
          const event = new CustomEvent('directOpenModal', { 
            detail: { 
              content: artifact,
              type: "Chat Message" 
            } 
          });
          mobileLayoutElement.dispatchEvent(event);
  
          console.log("🚨 Dispatched directOpenModal event to MobileLayout");
        } catch (eventError) {
          console.error("🚨 Error dispatching event:", eventError);
          // Fallback to the original approach on event dispatch error
          if (handleAddToProject) {
            handleAddToProject({ content: artifact });
          }
        }
      } else {
        // Fallback to the original approach
        console.log("🚨 MobileLayout not found, using original approach");
        if (handleAddToProject) {
          handleAddToProject({ content: artifact });
        } else {
          console.error("🚨 ERROR: handleAddToProject function is not available");
          toast.error("Could not open Add to Project panel. Please try again.");
        }
      }
    } catch (error) {
      console.error("🚨 Error in handleProjectAdd:", error);
      toast.error("Error opening Add to Project panel. Please try again.");
    }
  };

  return (
    <Box
      sx={{
        border: `1px solid ${botColor}40`,
        borderRadius: 2,
        overflow: 'hidden',
        bgcolor: `${botColor}15`,
        my: 2,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header with tabs */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: `1px solid ${botColor}40`,
          px: 2,
          bgcolor: `${botColor}20`,
        }}
      >
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          sx={{
            minHeight: 48,
            '& .MuiTab-root': {
              color: 'text.secondary',
              minHeight: 48,
              py: 1,
              px: 2,
              '&.Mui-selected': {
                color: botColor,
              },
            },
            '& .MuiTabs-indicator': {
              backgroundColor: botColor,
            },
          }}
        >
          <Tab value="visual" label="Visual" />
          <Tab value="code" label="Code" />
        </Tabs>
        <Box sx={{ display: 'flex' }}>
          <Tooltip title={copied ? "Copied!" : "Copy to clipboard"} placement="top">
            <IconButton
              onClick={handleCopy}
              size="small"
              sx={{
                color: copied ? 'success.main' : `${botColor}CC`,
                '&:hover': {
                  bgcolor: `${botColor}25`,
                },
              }}
            >
              {copied ? <CheckIcon /> : <ContentCopyIcon />}
            </IconButton>
          </Tooltip>
          
          {/* Enhanced folder icon button with higher priority */}
          <Tooltip title="Add to Project" placement="top">
            <Button
              onClick={handleProjectAdd}
              size="small"
              startIcon={<FolderIcon />}
              variant="contained"
              sx={{
                bgcolor: `${botColor}`,
                color: 'white',
                ml: 1,
                '&:hover': {
                  bgcolor: `${botColor}DD`,
                },
                zIndex: 1, // Ensure button is clickable
              }}
            >
              Save
            </Button>
          </Tooltip>
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{ 
        p: 3,
        flex: 1,
        minHeight: 0,
        bgcolor: activeTab === 'code' ? 'background.paper' : `${botColor}15`,
      }}>
        {activeTab === 'visual' ? (
          <Box
            dangerouslySetInnerHTML={{ __html: artifact }}
            sx={{ 
              mt: 2,
              '& *': { // Apply styles to all HTML elements
                maxWidth: '100%',
                wordWrap: 'break-word',
                overflowWrap: 'break-word',
              },
              '& img': { // Style images
                maxWidth: '100%',
                height: 'auto',
              },
              '& table': { // Style tables
                width: '100%',
                borderCollapse: 'collapse',
                marginBottom: '1rem',
              },
              '& th, & td': {
                border: '1px solid rgba(255, 255, 255, 0.1)',
                padding: '8px',
              },
              '& a': { // Style links
                color: botColor,
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline',
                },
              },
            }}
          />
        ) : (
          <Box
            component="pre"
            sx={{
              m: 0,
              mt: 2,
              p: 2,
              bgcolor: 'background.paper',
              borderRadius: 1,
              overflow: 'auto',
              width: '100%',
              whiteSpace: 'pre-wrap',
              wordWrap: 'break-word',
              '&::-webkit-scrollbar': {
                height: '8px',
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                background: 'transparent',
              },
              '&::-webkit-scrollbar-thumb': {
                background: `${botColor}40`,
                borderRadius: '4px',
              },
            }}
          >
            <code style={{ 
              display: 'block', 
              width: '100%',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word'
            }}>{artifact}</code>
          </Box>
        )}
      </Box>
    </Box>
  );
};

const renderMessage = (content, bot, handlers = {}) => {
  const { handleCopyMessage, handleAddToProject } = handlers;
  
  if (!content.includes('`') && !content.includes('#') && !content.includes('*') && !content.includes('[artifact]')) {
    // For plain text, split by newlines and create paragraphs
    return content.split('\n').map((line, i) => (
      <Typography key={i} sx={{ mb: line ? 1 : 0 }}>
        {line || '\u00A0'}
      </Typography>
    ));
  }

  // Split content into parts based on artifact tags
  const parts = content.split(/(\[artifact\].*?\[\/artifact\])/s);
  
  return (
    <Box sx={{ position: 'relative', width: '100%' }}>
      {parts.map((part, index) => {
        if (part.startsWith('[artifact]') && part.endsWith('[/artifact]')) {
          // Extract artifact content
          const artifactContent = part.slice(10, -11).trim();
          return <ArtifactDisplay key={index} artifact={artifactContent} botColor={bot.botColor} handleAddToProject={handleAddToProject} />;
        } else {
          // Render regular markdown content
          return (
            <ReactMarkdown
              key={index}
              components={{
                p: ({ children }) => (
                  <Typography sx={{ mb: 1 }}>{children}</Typography>
                ),
                code({node, inline, className, children, ...props}) {
                  const match = /language-(\w+)/.exec(className || '');
                  return !inline && match ? (
                    <SyntaxHighlighter
                      style={tomorrow}
                      language={match[1]}
                      PreTag="div"
                      {...props}
                    >
                      {String(children).replace(/\n$/, '')}
                    </SyntaxHighlighter>
                  ) : (
                    <code className={className} {...props}>
                      {children}
                    </code>
                  );
                }
              }}
            >
              {part}
            </ReactMarkdown>
          );
        }
      })}
      
      <Box sx={{ 
        position: 'absolute', 
        right: 2, 
        top: 2, 
        display: 'flex',
        opacity: 0,
        transition: 'opacity 0.2s',
        '.message-bubble:hover &': {
          opacity: 1
        }
      }}>
        <IconButton 
          size="small" 
          onClick={() => handleCopyMessage?.(content)}
          sx={{ color: 'grey.500', '&:hover': { color: 'primary.main' } }}
        >
          <ContentCopyIcon fontSize="small" />
        </IconButton>
        <IconButton 
          size="small" 
          onClick={() => handleAddToProject?.({ content })}
          sx={{ color: 'grey.500', '&:hover': { color: 'primary.main' } }}
          title="Add to Project"
        >
          <FolderIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
};

// Mobile/Tablet Layout Component
const MobileLayout = ({ 
  bot, 
  threads, 
  messages, 
  loading,
  formLoading, 
  handleSendMessage, 
  handleDeleteThread,
  handleFormSubmit,
  handleTaskSelect,
  renderMessage,
  handleThreadSelection,
  language,
  getLanguageName
}) => {
  const [activeTab, setActiveTab] = useState('tasks');
  const [selectedThread, setSelectedThread] = useState(null);
  const messageInputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [showMobileAddToProjectModal, setShowMobileAddToProjectModal] = useState(false);
  const [selectedContent, setSelectedContent] = useState(null);
  
  // Reference to the component root element for event listening
  const componentRef = useRef(null);
  
  // Listen for openAddToProjectModal custom events
  useEffect(() => {
    const handleOpenModal = (event) => {
      console.log('🚨 MOBILE: Received openAddToProjectModal event', event.detail);
      const { content, type } = event.detail;
      
      // Set the content
      setSelectedContent({
        contentValue: content,
        contentType: type || selectedTask?.name || "Chat Message"
      });
      
      // Show the modal with debug info
      console.log('🚨 MOBILE: Setting modal to OPEN from custom event');
      setShowMobileAddToProjectModal(true);
      
    };
    
    // Handler for direct modal opening (from save button)
    const handleDirectOpenModal = (event) => {
      console.log('🚨 MOBILE: Received directOpenModal event', event.detail);
      const { content, type } = event.detail;
      
      // Set the content directly 
      setSelectedContent({
        contentValue: content,
        contentType: type || selectedTask?.name || "Chat Message"
      });
      
      // Force show the modal
      console.log('🚨 MOBILE: DIRECTLY setting modal to OPEN');
      setShowMobileAddToProjectModal(true);
      
      // Force visibility through DOM if component isn't responding
      setTimeout(() => {
        const modalElement = document.querySelector('.MuiModal-root');
        if (modalElement) {
          console.log('🚨 MOBILE: Found modal element, focusing');
          modalElement.setAttribute('tabindex', '0');
          modalElement.focus();
        }
      }, 300);
    };
    
    // Add the event listeners to the component root
    const element = componentRef.current;
    if (element) {
      console.log('🚨 MOBILE: Adding event listeners for modal opening');
      element.addEventListener('openAddToProjectModal', handleOpenModal);
      element.addEventListener('directOpenModal', handleDirectOpenModal); // Add the new listener
    }
    
    // Clean up event listeners
    return () => {
      if (element) {
        element.removeEventListener('openAddToProjectModal', handleOpenModal);
        element.removeEventListener('directOpenModal', handleDirectOpenModal);
      }
    };
  }, [selectedTask]);
  
  // Define handleCopyMessage and handleAddToProject locally since they're needed in renderMessage
  const handleCopyMessage = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Content copied to clipboard");
  };
  
  const handleAddToProject = (message) => {
    console.log('🚨 MOBILE HANDLER CALLED with message:', message); 
    
    // Extract content safely
    const content = typeof message === 'string' 
      ? message 
      : (message?.content || 'No content available');
    
    // Set content for project modal with visual feedback
    console.log('🚨 MOBILE: Setting content:', content.substring(0, 50) + '...');
    
    // Update the state
    setSelectedContent({
      contentValue: content,
      contentType: selectedTask?.name || "Chat Message"
    });
    
    // Force display the modal with visual feedback
    console.log('🚨 MOBILE: About to show modal - CURRENT STATE:', showMobileAddToProjectModal);
    setShowMobileAddToProjectModal(true);
    console.log('🚨 MOBILE: Modal state set to TRUE');
    
    // Force visibility through DOM if component isn't responding
    try {
      // Force focus on modal when it opens
      setTimeout(() => {
        const modalElement = document.querySelector('.MuiModal-root');
        if (modalElement) {
          console.log('🚨 MOBILE: Found modal element, focusing');
          modalElement.setAttribute('tabindex', '0');
          modalElement.focus();
        } else {
          console.log('🚨 MOBILE: Modal element not found in DOM');
        }
      }, 300);
    } catch (error) {
      console.error('🚨 MOBILE: Error forcing modal focus:', error);
    }
  };

  // Add useEffect for scrolling
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  // Add useEffect to sync with parent thread state
  useEffect(() => {
    if (messages.length > 0 && !selectedThread) {
      const threadId = messages[0].thread_id;
      const thread = threads.find(t => t.threadId === threadId);
      if (thread) {
        setSelectedThread(thread);
        setActiveTab('chat');
      }
    }
  }, [messages, threads]);

  // Update the thread selection handler
  const handleThreadSelect = async (thread) => {
    setSelectedThread(thread);
    setActiveTab('chat');
    await handleThreadSelection(thread);
  };

  return (
    <Box 
      ref={componentRef}
      data-component="MobileLayout"
      sx={{ 
        height: 'calc(100vh - 65px)',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.default',
        position: 'relative',
        overflow: 'hidden' // Prevent overflow
      }}
    >
      {/* Bot Info Section */}
      <Box sx={{ 
        borderBottom: '1px solid',
        borderColor: 'divider',
        background: `linear-gradient(135deg, ${bot.botColor}20 0%, ${bot.botColor}35 100%)`,
        px: 2,
        pt: 4, // Increased top padding
        pb: 2, // Reduced bottom padding
      }}>
        <Box sx={{ 
          display: 'flex',
          alignItems: 'flex-start',
          gap: 3,
        }}>
          {/* Left side - Image and Name */}
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}>
            <Avatar
              src={characterImages[bot.botId]}
              sx={{ 
                width: 100, 
                height: 100,
                marginTop: '10px',
                border: `2px solid ${bot.botColor}`,
                boxShadow: `0 4px 20px -8px ${bot.botColor}80`
              }}
            >
              {bot?.name?.charAt(0) || 'B'}
            </Avatar>
            <Typography variant="h6" sx={{ 
              fontWeight: 600, 
              color: bot.botColor,
              textAlign: 'center',
              fontSize: '1.1rem'
            }}>
              {bot.name}
            </Typography>
          </Box>
          
          {/* Right side - Group and Description */}
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1" sx={{ 
              color: bot.botColor,
              opacity: 0.9,
              fontSize: '1rem',
              marginTop: '10px',
              fontWeight: 600,
              mb: 1
            }}>
              {bot.botName}, {bot.botGroup}
            </Typography>
            <Typography variant="body2" sx={{ 
              color: '#ffffff',
              opacity: 0.85,
              lineHeight: 1.5,
              fontSize: '0.9rem'
            }}>
              {bot.botDescription}
            </Typography>
          </Box>
        </Box>
      </Box>
      
      {/* Tab Navigation */}
      <Box sx={{ px: 2, mt: 2 }}>
        <Tabs 
          value={activeTab} 
          onChange={(e, newValue) => setActiveTab(newValue)}
          variant="fullWidth"
          sx={{ 
            mb: 2,
            '& .MuiTab-root': {
              color: 'text.secondary',
              '&.Mui-selected': {
                color: bot.botColor,
              },
            },
            '& .MuiTabs-indicator': {
              backgroundColor: bot.botColor,
            },
          }}
        >
          <Tab value="tasks" label="Tasks" />
          <Tab value="chat" label="Chat" disabled={!selectedThread} />
          <Tab value="history" label="History" />
        </Tabs>
      </Box>

      {/* Scrollable Content Area */}
      <Box sx={{ 
        flex: 1,
        overflow: 'hidden',
        position: 'relative',
        minHeight: 0,
        maxHeight: 'calc(100vh - 200px)', // Ensure it doesn't overflow
        display: 'flex',
        flexDirection: 'column',
        scrollbarWidth: 'thin',
        scrollbarColor: `${bot.botColor}80 transparent`,
        '&::-webkit-scrollbar': {
          width: '8px',
        },
        '&::-webkit-scrollbar-track': {
          background: 'transparent',
        },
        '&::-webkit-scrollbar-thumb': {
          background: `${bot.botColor}80`,
          borderRadius: '4px',
        },
        '&::-webkit-scrollbar-thumb:hover': {
          background: bot.botColor,
        },
      }}>
        {activeTab === 'tasks' && (
          <Box sx={{ 
            height: '100%',
            overflowY: 'auto',
            px: 2,
            pb: 2,
            flexGrow: 1,
            scrollbarWidth: 'thin',
            scrollbarColor: `${bot.botColor}80 transparent`,
            '&&::-webkit-scrollbar': {
              width: '8px',
            },
            '&&::-webkit-scrollbar-track': {
              background: 'transparent',
            },
            '&&::-webkit-scrollbar-thumb': {
              background: `${bot.botColor}80`,
              borderRadius: '4px',
            },
            '&&::-webkit-scrollbar-thumb:hover': {
              background: bot.botColor,
            },
          }}>
            <Grid container spacing={2}>
              {bot.tasks.map((task, index) => (
                <Grid item xs={12} sm={6} key={index}>
                  <Card
                    sx={{
                      backgroundColor: 'rgba(18, 18, 18, 0.8)',
                      borderRadius: '12px',
                      border: `1px solid ${bot.botColor}30`,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      cursor: 'pointer',
                      mt: index === 0 ? 1.25 : 0,
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: `0 8px 28px -6px ${bot.botColor}40`,
                      }
                    }}
                    onClick={async () => {
                      await handleTaskSelect(task);
                      setActiveTab('chat');
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                        <Box
                          sx={{
                            width: 44,
                            height: 44,
                            borderRadius: '12px',
                            backgroundColor: 'rgba(40, 40, 40, 0.8)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}
                        >
                          <i 
                            className={task.taskIcon}
                            style={{ 
                              color: bot.botColor, 
                              fontSize: '1.25rem',
                            }} 
                          />
                        </Box>
                        <Box>
                          <Typography 
                            variant="subtitle1" 
                            sx={{ 
                              fontWeight: 600,
                              color: '#ffffff',
                              mb: 1,
                            }}
                          >
                            {task.taskTitle}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              color: bot.botColor,
                              opacity: 0.8,
                              display: 'block',
                              mb: 1,
                              fontSize: '0.75rem',
                            }}
                          >
                            {task.aiCreditsToCharge} Credits
                          </Typography>
                          <Typography 
                            variant="body2"
                            sx={{
                              color: 'rgba(255, 255, 255, 0.7)',
                              fontSize: '0.875rem',
                              lineHeight: 1.5,
                            }}
                          >
                            {task.taskDescription}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {activeTab === 'chat' && selectedThread && (
          <Box sx={{ 
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            maxHeight: '100%', // Ensure it doesn't overflow
            overflow: 'hidden',
          }}>
            {language && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  pt: 3,
                  pb: 1,
                  backgroundColor: `${bot.botColor}CC`,
                  color: 'white',
                  borderTopLeftRadius: 0,
                  borderTopRightRadius: 0,
                  position: 'sticky',
                  top: 0,
                  zIndex: 10,
                  flexShrink: 0, // Prevent shrinking
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 500, fontSize: '1rem' }}>
                  Chosen Language: <Box component="span" sx={{ fontWeight: 700 }}>{getLanguageName(language)}</Box>{' '}
                  <Box 
                    component="span" 
                    sx={{ 
                      textDecoration: 'underline', 
                      cursor: 'pointer',
                      ml: 1,
                      fontSize: '0.85rem'
                    }}
                    onClick={() => window.dispatchEvent(new CustomEvent('openLanguageModal'))}
                  >
                    Update Language Preferences
                  </Box>
                </Typography>
              </Box>
            )}
            <Box sx={{ 
              flex: 1,
              overflowY: 'auto',
              px: 2,
              pb: 2,
              maxHeight: 'calc(100% - 60px)', // Account for input area
              scrollbarWidth: 'thin',
              scrollbarColor: `${bot.botColor}80 transparent`,
              '&&::-webkit-scrollbar': {
                width: '8px',
              },
              '&&::-webkit-scrollbar-track': {
                background: 'transparent',
              },
              '&&::-webkit-scrollbar-thumb': {
                background: `${bot.botColor}80`,
                borderRadius: '4px',
              },
              '&&::-webkit-scrollbar-thumb:hover': {
                background: bot.botColor,
              },
            }}>
              <Box sx={{ height: 10 }} />
              {messages.length === 0 ? (
                <Box sx={{ p: 2, textAlign: 'center' }}>
                  <Typography color="text.secondary">No messages yet</Typography>
                </Box>
              ) : (
                messages.map((message, index) => (
                  <Box
                    key={message.id}
                    sx={{
                      display: "flex",
                      mb: 1.5,
                      mt: index === 0 ? 2 : 0,
                      justifyContent: message.role === "user" ? "flex-end" : "flex-start",
                    }}
                  >
                    {message.role !== "user" && (
                      <Avatar
                        src={characterImages[bot.botId]}
                        sx={{ 
                          width: 40, 
                          height: 40, 
                          mr: 2,
                          border: `1px solid ${bot.botColor}40`,
                        }}
                      />
                    )}
                    <Box sx={{ maxWidth: "85%" }}>
                      <Paper
                        sx={{
                          p: 2,
                          bgcolor: message.role === "user" ? "rgba(190, 88, 218, 0.2)" : `${bot.botColor}10`,
                          color: message.role === "user" ? "#ffffff" : "text.primary",
                          borderRadius: 2,
                          border: "1px solid",
                          borderColor: message.role === "user" ? "#BE58DA" : `${bot.botColor}40`,
                        }}
                      >
                        {message.metadata?.isForm ? (
                          <DynamicForm
                            fields={message.metadata.formFields}
                            onSubmit={handleFormSubmit}
                            loading={loading}
                            bot={bot}
                            message={message}
                          />
                        ) : (
                          message.content[0]?.text?.value ? 
                            renderMessage(message.content[0].text.value, bot, { handleCopyMessage, handleAddToProject }) :
                            <Typography>Message content unavailable</Typography>
                        )}
                      </Paper>
                      <Box sx={{ 
                        mt: 0.5, 
                        display: 'flex', 
                        justifyContent: message.role === "user" ? "flex-end" : "flex-start"
                      }}>
                        <TimeAgo 
                          timestamp={message.created_at}
                          sx={{ 
                            color: 'text.secondary',
                            fontSize: '0.75rem'
                          }}
                        />
                      </Box>
                    </Box>
                    {message.role === "user" && (
                      <Avatar 
                        sx={{ 
                          width: 40, 
                          height: 40, 
                          ml: 2,
                          bgcolor: `${bot.botColor}20`,
                          color: bot.botColor,
                        }}
                      >
                        <PersonIcon />
                      </Avatar>
                    )}
                  </Box>
                ))
              )}
              {(loading || formLoading) && messages.length > 0 && (
                <Box
                  sx={{
                    display: "flex",
                    mb: 1,
                    alignItems: "flex-start",
                  }}
                >
                  <Avatar
                    src={characterImages[bot.botId]}
                    sx={{ 
                      width: 40, 
                      height: 40, 
                      mr: 2,
                      border: `1px solid ${bot.botColor}40`,
                    }}
                  />
                  <Paper
                    sx={{
                      p: 2,
                      bgcolor: "background.paper",
                      borderRadius: 2,
                    }}
                  >
                    <LoadingDots />
                  </Paper>
                </Box>
              )}
              <div ref={messagesEndRef} style={{ height: '1px', marginTop: '4px' }} />
            </Box>
            <Box sx={{
              p: 2,
              bgcolor: 'background.default',
              borderTop: '1px solid',
              borderColor: 'divider',
            }}>
              <MessageInput 
                onSendMessage={handleSendMessage}
                loading={loading}
                inputRef={messageInputRef}
                bot={bot}
                selectedTask={selectedTask}
              />
            </Box>
          </Box>
        )}

        {activeTab === 'history' && (
          <Box sx={{ 
            height: '100%',
            overflowY: 'auto',
            scrollbarWidth: 'thin',
            scrollbarColor: `${bot.botColor}80 transparent`,
            '&&::-webkit-scrollbar': {
              width: '8px',
            },
            '&&::-webkit-scrollbar-track': {
              background: 'transparent',
            },
            '&&::-webkit-scrollbar-thumb': {
              background: `${bot.botColor}80`,
              borderRadius: '4px',
            },
            '&&::-webkit-scrollbar-thumb:hover': {
              background: bot.botColor,
            },
          }}>
            <List>
              {threads.map((thread) => (
                <ListItem
                  key={thread.threadId}
                  sx={{
                    px: 2,
                    py: 2,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <ListItemText
                    primary={thread.title}
                    secondary={thread.lastMessage}
                    onClick={() => handleThreadSelect(thread)}
                    sx={{ cursor: 'pointer' }}
                  />
                  <IconButton
                    edge="end"
                    onClick={() => handleDeleteThread(thread)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </ListItem>
              ))}
            </List>
          </Box>
        )}
      </Box>
      
      {/* Add the AddToProjectModal to mobile layout */}
      <MobileAddToProjectModal
        open={showMobileAddToProjectModal}
        onClose={() => {
          console.log('🚨 MOBILE: Closing project modal');
          setShowMobileAddToProjectModal(false);
        }}
        contentType={selectedContent?.contentType}
        contentValue={selectedContent?.contentValue}
        botTaskName={selectedTask?.name}
      />
    </Box>
  );
};

// Main BotChat component
const BotChat = () => {
  const { botId } = useParams();
  const [searchParams] = useSearchParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery('(max-width:1300px)');
  const { fetchLimits } = useLimits();
  const { language } = useLanguage();
  
  // Remove the global styles for dialogs since we're using a dedicated mobile component
  
  const [mobileOpen, setMobileOpen] = useState(false);
  const [threads, setThreads] = useState([]);
  const [selectedThread, setSelectedThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [threadToDelete, setThreadToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const messagesEndRef = useRef(null);
  const messageInputRef = useRef(null);
  const [activeTab, setActiveTab] = useState(0);
  const [selectedTask, setSelectedTask] = useState(null);
  const [bot, setBot] = useState(null);
  const [showAddToProjectModal, setShowAddToProjectModal] = useState(false);
  const [selectedContent, setSelectedContent] = useState(null);

  // Get language name from language code
  const getLanguageName = (languageCode) => {
    if (!languageCode) return null;
    const languageObj = languages.find(lang => lang.code === languageCode);
    return languageObj ? languageObj.name : languageCode;
  };

  // Add handleCopyMessage function
  const handleCopyMessage = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Content copied to clipboard");
  };

  // Add handleAddToProject function for the main BotChat component
  const handleAddToProject = (message) => {
    // Handle different message formats (string or object)
    const content = typeof message === 'string' 
      ? message 
      : (message?.content || message?.contentValue || JSON.stringify(message));
    
    // Set content for project modal with proper type
    setSelectedContent({
      contentValue: content,
      contentType: selectedTask?.taskTitle || "Chat Message",
      botTaskName: undefined // Remove this as we'll use contentType consistently
    });
    
    // Show the appropriate modal based on device type
    if (isMobile) {
      setShowAddToProjectModal(true);
    } else {
      setShowAddToProjectModal(true); // Using the same state for both mobile and desktop
    }
  };

  // Add event listener for addToProject custom event (from mobile layout)
  useEffect(() => {
    const handleAddToProjectEvent = (event) => {
      console.log('Received addToProject event:', event.detail); // Debug log
      
      const { content } = event.detail;
      if (!content) {
        console.error('Invalid message format in addToProject event', event.detail);
        toast.error('Failed to add to project: Invalid content format');
        return;
      }
      
      // Extract the actual content value based on the format
      const contentValue = typeof content === 'string' 
        ? content 
        : Array.isArray(content) 
          ? content[0]?.text?.value
          : content;
          
      if (!contentValue) {
        console.error('Could not extract content value from:', content);
        toast.error('Failed to add to project: Could not extract content');
        return;
      }
      
      console.log('Setting content value:', contentValue); // Debug log
      
      setSelectedContent({
        contentValue: contentValue,
        contentType: selectedTask?.name || "Chat Message"
      });
      setShowAddToProjectModal(true);
    };

    window.addEventListener('addToProject', handleAddToProjectEvent);
    
    return () => {
      window.removeEventListener('addToProject', handleAddToProjectEvent);
    };
  }, [selectedTask]);

  const scrollToBottom = () => {
    const messagesEnd = messagesEndRef.current;
    if (messagesEnd) {
      const scrollableContainer = messagesEnd.parentElement;
      if (scrollableContainer) {
        scrollableContainer.scrollTop = scrollableContainer.scrollHeight;
      }
    }
  };

  // Add helper function to create initial task messages
  const createInitialTaskMessages = (threadId) => {
    const thread = threads.find(t => t.threadId === threadId);
    if (thread && thread.title) {
      // Find the matching task based on the thread title
      const task = bot.tasks.find(t => t.taskTitle === thread.title);
      
      if (task) {
        // Set the selected task
        setSelectedTask(task);
        
        // Create initial messages for task thread
        const userMessage = {
          id: Date.now().toString(),
          object: "thread.message",
          created_at: Math.floor(Date.now() / 1000),
          thread_id: threadId,
          role: 'user',
          content: [{ 
            type: 'text', 
            text: { value: thread.title }
          }],
          metadata: {},
          attachments: []
        };

        const botMessage = {
          id: `${Date.now()}-bot`,
          object: "thread.message",
          created_at: Math.floor(Date.now() / 1000),
          thread_id: threadId,
          role: 'assistant',
          content: [{ 
            type: 'text', 
            text: { value: task.userInputDescription || 'Please fill out this info to get you started:' }
          }],
          metadata: { 
            isForm: true, 
            formFields: task.formFieldsInput || [],
            isEditable: true
          },
          attachments: []
        };

        setMessages([userMessage, botMessage]);
      }
    }
  };

  const loadMessages = async (threadId) => {
    try {
      setLoading(true);
      const response = await generalApi.chat.getMessages(threadId);
      if (Array.isArray(response) && response.length > 0) {
        const validMessages = response
          .filter(msg => 
            msg.content && 
            Array.isArray(msg.content) && 
            msg.content.length > 0 &&
            msg.content[0].type === 'text' &&
            msg.content[0].text?.value
          )
          .sort((a, b) => a.created_at - b.created_at);
        
        if (validMessages.length > 0) {
          setMessages(validMessages);
          
          // Get the thread details which includes taskTitle and assistantId
          const thread = threads.find(t => t.threadId === threadId);
          if (thread?.taskTitle) {
            // Find the matching task in the bot's tasks
            const matchingTask = bot.tasks.find(t => 
              t.taskTitle === thread.taskTitle && 
              (!t.assistantId || t.assistantId === thread.assistantId)
            );
            
            if (matchingTask) {
              console.log('Found task from thread:', matchingTask);
              setSelectedTask(matchingTask);
            } else {
              // If no exact match found, try matching just by taskTitle
              const taskByTitle = bot.tasks.find(t => t.taskTitle === thread.taskTitle);
              if (taskByTitle) {
                console.log('Found task by title:', taskByTitle);
                setSelectedTask(taskByTitle);
              }
            }
          }
        } else {
          createInitialTaskMessages(threadId);
        }
      } else {
        createInitialTaskMessages(threadId);
      }
    } catch (error) {
      console.error('Failed to load messages:', error);
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const loadThreads = async () => {
    try {
      const response = await generalApi.chat.getThreads(botId);
      if (Array.isArray(response)) {
        const filteredThreads = response.filter(thread => thread.botId === parseInt(botId));
        setThreads(filteredThreads);
      } else {
        setThreads([]);
      }
    } catch (error) {
      console.error('Failed to load threads:', error);
      toast.error('Failed to load chats');
      setThreads([]);
    }
  };

  useEffect(() => {
    const loadInitialThread = async () => {
      const threadId = searchParams.get('thread');
      if (threadId) {
        try {
          const response = await generalApi.chat.getThreads();
          if (Array.isArray(response)) {
            const thread = response.find(t => t.threadId === threadId);
            if (thread) {
              setSelectedThread(thread);
              if (isMobile || isTablet) {
                setActiveTab('chat'); // For mobile layout
              } else {
                setActiveTab(1); // For desktop layout
              }
              await loadMessages(thread.threadId);
            }
          }
        } catch (error) {
          console.error('Failed to load initial thread:', error);
          toast.error('Failed to load chat');
        }
      }
    };

    loadThreads();
    loadInitialThread();
  }, [botId, searchParams, isMobile, isTablet]);

  useEffect(() => {
    if (selectedThread?.threadId) {
      loadMessages(selectedThread.threadId);
    }
  }, [selectedThread?.threadId]);

  useEffect(() => {
    // Load bot data when component mounts
    const loadBotData = async () => {
      try {
        const botsData = await import('../const/bots.json');
        const foundBot = botsData.default.find(b => b.botId === parseInt(botId));
        if (foundBot) {
          setBot(foundBot);
        } else {
          toast.error('Bot not found');
        }
      } catch (error) {
        console.error('Failed to load bot data:', error);
        toast.error('Failed to load bot configuration');
      }
    };
    loadBotData();
  }, [botId]);

  // Find the bot data
  if (!bot) {
    return <Typography>Bot not found</Typography>;
  }

  const handleDeleteThread = async () => {
    if (!threadToDelete) return;

    try {
      setIsDeleting(true);
      await generalApi.chat.deleteThread(threadToDelete.threadId);
      setThreads(threads.filter(t => t.threadId !== threadToDelete.threadId));
      if (selectedThread?.threadId === threadToDelete.threadId) {
        setSelectedThread(null);
        setMessages([]);
      }
      setDeleteDialogOpen(false);
      setThreadToDelete(null);
    } catch (error) {
      console.error('Failed to delete thread:', error);
      toast.error('Failed to delete chat');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleTaskSelect = async (task) => {
    try {
      // Create a new thread with bot and task information
      const response = await generalApi.chat.createThread({
        botId: parseInt(botId),
        taskTitle: task.taskTitle,
        title: task.taskTitle
      });

      if (response?.threadId) {
        const newThread = {
          ...response,
          title: task.taskTitle
        };
        setSelectedThread(newThread);
        setSelectedTask(task);
        setThreads([newThread, ...threads]);

        // Add initial messages
        const userMessage = {
          id: Date.now().toString(),
          object: "thread.message",
          created_at: Math.floor(Date.now() / 1000),
          thread_id: newThread.threadId,
          role: 'user',
          content: [{ 
            type: 'text', 
            text: { value: task.taskTitle }
          }],
          metadata: {},
          attachments: []
        };

        const botMessage = {
          id: `${Date.now()}-bot`,
          object: "thread.message",
          created_at: Math.floor(Date.now() / 1000),
          thread_id: newThread.threadId,
          role: 'assistant',
          content: [{ 
            type: 'text', 
            text: { value: task.userInputDescription || 'Please fill out this info to get you started:' }
          }],
          metadata: { 
            isForm: true, 
            formFields: task.formFieldsInput || [],
            isEditable: true
          },
          attachments: []
        };

        setMessages([userMessage, botMessage]);
        setActiveTab(1); // Switch to chat tab if using tabs
      }
    } catch (error) {
      console.error('Failed to create thread:', error);
      toast.error('Failed to create new chat');
    }
  };

  const handleSendMessage = async (messageText) => {
    try {
      // Validate message length (300 words max)
      const wordCount = messageText.trim().split(/\s+/).length;
      if (wordCount > 300) {
        toast.error("Message exceeds maximum length of 300 words");
        return;
      }

      setLoading(true);

      if (!messageText.trim() || !selectedThread) return;
      
      // Add user message immediately
      const userMessage = {
        id: Date.now().toString(),
        object: "thread.message",
        created_at: Math.floor(Date.now() / 1000),
        thread_id: selectedThread.threadId,
        role: 'user',
        content: [{ 
          type: 'text', 
          text: { value: messageText }
        }],
        metadata: {},
        attachments: []
      };
      
      // Set user message and scroll
      setMessages(prevMessages => {
        const newMessages = [...prevMessages, userMessage];
        // Wait for message to render then scroll
        setTimeout(scrollToBottom, 100);
        return newMessages;
      });

      if (!selectedThread.lastMessageAt) {
        const title = messageText.split(' ').slice(0, 5).join(' ') + '...';
        const updatedThread = { 
          ...selectedThread, 
          title, 
          lastMessage: messageText,
          lastMessageAt: new Date().toISOString()
        };
        setSelectedThread(updatedThread);
        setThreads(threads.map(t => 
          t.threadId === selectedThread.threadId ? updatedThread : t
        ));
      }

      const response = await generalApi.chat.sendMessage(
        selectedThread.threadId,
        messageText,
        true // This is the first message in the thread
      );

      if (response?.message) {
        const assistantMessage = {
          id: `${Date.now()}-assistant`,
          object: "thread.message",
          created_at: Math.floor(Date.now() / 1000),
          thread_id: selectedThread.threadId,
          role: 'assistant',
          content: [{ 
            type: 'text', 
            text: { value: response.message }
          }],
          metadata: {},
          attachments: []
        };

        setMessages(prevMessages => {
          const newMessages = [...prevMessages, assistantMessage];
          // Wait for message to render then scroll
          setTimeout(scrollToBottom, 100);
          return newMessages;
        });
      }
      
      await loadThreads(false);

      // Fetch limits after response
      await fetchLimits(true);
      
      // Set up a delayed fetch after 2 seconds
      await new Promise(resolve => setTimeout(resolve, 2000));
      await fetchLimits(true);

    } catch (error) {
      toast.error('Failed to send message');
      console.error('Send message error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFormSubmit = async (formData) => {
    if (!selectedThread || !selectedTask) return;

    // Format form data with one field per line
    const formattedData = Object.entries(formData)
      .map(([key, value]) => `${key}:\n${value}`)
      .join('\n\n');

    const message = `${selectedTask.taskTitle}\n\n${formattedData}`;

    try {
      // First disable the form
      setMessages(prevMessages => {
        const updatedMessages = prevMessages.map(msg => {
          if (msg.metadata?.isForm) {
            return {
              ...msg,
              metadata: {
                ...msg.metadata,
                isEditable: false
              }
            };
          }
          return msg;
        });
        return updatedMessages;
      });

      // Create and add user message
      const userMessage = {
        id: Date.now().toString(),
        object: "thread.message",
        created_at: Math.floor(Date.now() / 1000),
        thread_id: selectedThread.threadId,
        role: 'user',
        content: [{ 
          type: 'text', 
          text: { value: message }
        }],
        metadata: {},
        attachments: []
      };

      // Add user message and scroll
      setMessages(prevMessages => {
        const newMessages = [...prevMessages, userMessage];
        setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: "auto" }), 0);
        return newMessages;
      });

      // Now set loading states after user message is shown
      setLoading(true);
      setFormLoading(true);

      // Update thread info
      const updatedThread = { 
        ...selectedThread, 
        title: selectedTask.taskTitle,
        lastMessage: message,
        lastMessageAt: new Date().toISOString()
      };
      setSelectedThread(updatedThread);
      setThreads(threads.map(t => 
        t.threadId === selectedThread.threadId ? updatedThread : t
      ));

      // Send message and get response
      const response = await generalApi.chat.sendMessage(
        selectedThread.threadId,
        message,
        true // This is the first message in the thread
      );

      // Add assistant message after response
      if (response?.message) {
        const assistantMessage = {
          id: `${Date.now()}-assistant`,
          object: "thread.message",
          created_at: Math.floor(Date.now() / 1000),
          thread_id: selectedThread.threadId,
          role: 'assistant',
          content: [{ 
            type: 'text', 
            text: { value: response.message }
          }],
          metadata: {},
          attachments: []
        };

        // Add assistant message and scroll
        setMessages(prevMessages => {
          const newMessages = [...prevMessages, assistantMessage];
          setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: "auto" }), 0);
          return newMessages;
        });
      }

      await loadThreads(false);

      // Fetch limits after response
      await fetchLimits(true);
      
      // Set up a delayed fetch after 2 seconds
      await new Promise(resolve => setTimeout(resolve, 2000));
      await fetchLimits(true);

    } catch (error) {
      toast.error('Failed to send message');
      console.error('Send message error:', error);
    } finally {
      setLoading(false);
      setFormLoading(false);
      setSelectedTask(null);
    }
  };

  // Add a new function to handle thread selection
  const handleThreadSelection = async (thread) => {
    setSelectedThread(thread);
    await loadMessages(thread.threadId);
  };

  // Render mobile layout or desktop layout
  return (isMobile || isTablet) ? (
    <MobileLayout 
      bot={bot}
      threads={threads}
      messages={messages}
      loading={loading}
      formLoading={formLoading}
      handleSendMessage={handleSendMessage}
      handleDeleteThread={handleDeleteThread}
      handleFormSubmit={handleFormSubmit}
      handleTaskSelect={handleTaskSelect}
      renderMessage={(content, bot) => renderMessage(content, bot, { handleCopyMessage, handleAddToProject })}
      handleThreadSelection={handleThreadSelection}
      language={language}
      getLanguageName={getLanguageName}
    />
  ) : (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        pt: 2,
        pb: 2,
        px: { xs: 0, sm: 0, md: 0.5 },
        mt: { xs: 0, sm: 0, md: 3 },
        mx: { xs: 0, sm: 0, md: -3 },
        height: { xs: 'calc(100vh - 56px)', sm: 'calc(100vh - 64px)', md: 'calc(100vh - 100px)' },
        bgcolor: "background.default",
        overflow: "hidden",
      }}
    >
      <Box sx={{ 
        display: "flex", 
        gap: { xs: 0, sm: 0, md: 1 },
        flexGrow: 1, 
        height: "100%", 
        overflow: "hidden",
      }}>
        <Paper
          elevation={0}
          sx={{
            width: "352px",
            minWidth: "352px",
            flexShrink: 0,
            display: "flex",
            flexDirection: "column",
            height: "100%",
            overflow: "hidden",
            bgcolor: "background.default",
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          {/* Bot Info Section */}
          <Box sx={{ 
            borderBottom: '1px solid', 
            borderColor: 'divider',
            background: `linear-gradient(135deg, ${bot.botColor}20 0%, ${bot.botColor}35 100%)`,
          }}>
            <Box sx={{ height: 10 }} />
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 3, 
              mb: 4,
            }}>
              <Avatar
                src={characterImages[bot.botId]}
                sx={{ 
                  width: 120, 
                  height: 120,
                  border: `2px solid ${bot.botColor}`,
                  boxShadow: `0 4px 20px -8px ${bot.botColor}80`,
                  ml: 2,
                  flexShrink: 0,
                }}
              />
              <Box sx={{ 
                flexGrow: 1,
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                pr: 3,
                ml: -1,
              }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    color: '#ffffff', 
                    fontWeight: 600,
                    fontSize: '1.5rem',
                    width: 'max-content',
                    maxWidth: '100%',
                    minWidth: 0,
                  }}
                >
                  {bot.botName}
                </Typography>
                <Typography variant="subtitle2" sx={{ 
                  color: bot.botColor,
                  opacity: 0.9,
                  fontSize: '1.1rem',
                  wordWrap: 'break-word',
                  whiteSpace: 'normal',
                }}>
                  {bot.botGroup}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ px: 3, pb: 4 }}>
              <Typography variant="body2" sx={{ 
                color: '#ffffff',
                opacity: 0.85,
                lineHeight: 1.6,
                fontSize: '0.95rem',
              }}>
                {bot.botDescription}
              </Typography>
            </Box>
          </Box>

          {/* Tabs */}
          <Tabs 
            value={activeTab} 
            onChange={(e, newValue) => setActiveTab(newValue)}
            sx={{ 
              borderBottom: '1px solid',
              borderColor: 'divider',
              '& .MuiTab-root': {
                color: 'text.secondary',
                '&.Mui-selected': {
                  color: bot.botColor,
                },
              },
              '& .MuiTabs-indicator': {
                backgroundColor: bot.botColor,
              },
            }}
          >
            <Tab label="Tasks" />
            <Tab label="Chat History" />
          </Tabs>

          {/* Content based on active tab */}
          <Box 
            className="custom-scrollbar"
            sx={{ 
              flex: 1, 
              overflowY: 'auto',
              scrollbarWidth: 'thin',
              scrollbarColor: `${bot.botColor}80 transparent`,
              '&&::-webkit-scrollbar': {
                width: '8px',
              },
              '&&::-webkit-scrollbar-track': {
                background: 'transparent',
              },
              '&&::-webkit-scrollbar-thumb': {
                background: `${bot.botColor}80`,
                borderRadius: '4px',
              },
              '&&::-webkit-scrollbar-thumb:hover': {
                background: bot.botColor,
              },
            }}
          >
            {activeTab === 0 ? (
              // Tasks Section
              <Box sx={{ p: 3, pt: 2 }}>
                {bot.tasks.map((task, index) => (
                  <SidebarTaskCard
                    key={index}
                    task={task}
                    backgroundColor={bot.botColor}
                    onClick={() => handleTaskSelect(task)}
                  />
                ))}
              </Box>
            ) : (
              // Previous Chats Section
              <List>
                {threads.map((thread) => (
                  <ListItem
                    key={thread.threadId}
                    button
                    selected={selectedThread?.threadId === thread.threadId}
                    onClick={() => setSelectedThread(thread)}
                    sx={{
                      px: 3,
                      py: 2,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      '&.Mui-selected': {
                        backgroundColor: `${bot.botColor}15`,
                      },
                      '&:hover': {
                        backgroundColor: `${bot.botColor}10`,
                      }
                    }}
                  >
                    <ListItemText
                      primary={thread.title}
                      secondary={thread.lastMessage}
                      secondaryTypographyProps={{
                        noWrap: true,
                        sx: { color: 'text.secondary' }
                      }}
                    />
                    <IconButton
                      edge="end"
                      onClick={(e) => {
                        e.stopPropagation();
                        setThreadToDelete(thread);
                        setDeleteDialogOpen(true);
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            height: "100%",
            overflow: "hidden",
            bgcolor: "background.default",
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          {selectedThread ? (
            <>
              {language && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    pt: 3,
                    pb: 1,
                    backgroundColor: `${bot.botColor}CC`,
                    color: 'white',
                    borderTopLeftRadius: 0,
                    borderTopRightRadius: 0,
                    position: 'sticky',
                    top: 0,
                    zIndex: 10,
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 500, fontSize: '1rem' }}>
                    Chosen Language: <Box component="span" sx={{ fontWeight: 700 }}>{getLanguageName(language)}</Box>{' '}
                    <Box 
                      component="span" 
                      sx={{ 
                        textDecoration: 'underline', 
                        cursor: 'pointer',
                        ml: 1,
                        fontSize: '0.85rem'
                      }}
                      onClick={() => window.dispatchEvent(new CustomEvent('openLanguageModal'))}
                    >
                      Update Language Preferences
                    </Box>
                  </Typography>
                </Box>
              )}
              <Box
                sx={{
                  flexGrow: 1,
                  overflowY: "auto",
                  p: 3,
                  pt: 6,
                  bgcolor: "background.default",
                  scrollbarWidth: 'thin',
                  scrollbarColor: `${bot.botColor}80 transparent`,
                  '&&::-webkit-scrollbar': {
                    width: '8px',
                  },
                  '&&::-webkit-scrollbar-track': {
                    background: 'transparent',
                  },
                  '&&::-webkit-scrollbar-thumb': {
                    background: `${bot.botColor}80`,
                    borderRadius: '4px',
                  },
                  '&&::-webkit-scrollbar-thumb:hover': {
                    background: bot.botColor,
                  },
                }}
              >
                <Box sx={{ height: 10 }} />
                {messages.map((message, index) => (
                  <Box
                    key={message.id}
                    sx={{
                      display: "flex",
                      mb: 1.5,
                      mt: index === 0 ? 2 : 0,
                      justifyContent: message.role === "user" ? "flex-end" : "flex-start",
                    }}
                  >
                    {message.role !== "user" && (
                      <Avatar
                        src={characterImages[bot.botId]}
                        sx={{ 
                          width: 40, 
                          height: 40, 
                          mr: 2,
                          border: `1px solid ${bot.botColor}40`,
                        }}
                      />
                    )}
                    <Box sx={{ maxWidth: "85%" }}>
                      <Paper
                        sx={{
                          p: 2,
                          bgcolor: message.role === "user" ? "rgba(190, 88, 218, 0.2)" : `${bot.botColor}10`,
                          color: message.role === "user" ? "#ffffff" : "text.primary",
                          borderRadius: 2,
                          border: "1px solid",
                          borderColor: message.role === "user" ? "#BE58DA" : `${bot.botColor}40`,
                        }}
                      >
                        {message.metadata?.isForm ? (
                          <DynamicForm
                            fields={message.metadata.formFields}
                            onSubmit={handleFormSubmit}
                            loading={loading}
                            bot={bot}
                            message={message}
                          />
                        ) : (
                          message.content[0]?.text?.value ? 
                            renderMessage(message.content[0].text.value, bot, { handleCopyMessage, handleAddToProject }) :
                            <Typography>Message content unavailable</Typography>
                        )}
                      </Paper>
                      <Box sx={{ 
                        mt: 0.5, 
                        display: 'flex', 
                        justifyContent: message.role === "user" ? "flex-end" : "flex-start"
                      }}>
                        <TimeAgo 
                          timestamp={message.created_at}
                          sx={{ 
                            color: 'text.secondary',
                            fontSize: '0.75rem'
                          }}
                        />
                      </Box>
                    </Box>
                    {message.role === "user" && (
                      <Avatar 
                        sx={{ 
                          width: 40, 
                          height: 40, 
                          ml: 2,
                          bgcolor: `${bot.botColor}20`,
                          color: bot.botColor,
                        }}
                      >
                        <PersonIcon />
                      </Avatar>
                    )}
                  </Box>
                ))}
                {(loading || formLoading) && messages.length > 0 && (
                  <Box
                    sx={{
                      display: "flex",
                      mb: 1,
                      alignItems: "flex-start",
                    }}
                  >
                    <Avatar
                      src={characterImages[bot.botId]}
                      sx={{ 
                        width: 40, 
                        height: 40, 
                        mr: 2,
                        border: `1px solid ${bot.botColor}40`,
                      }}
                    />
                    <Paper
                      sx={{
                        p: 2,
                        bgcolor: "background.paper",
                        borderRadius: 2,
                      }}
                    >
                      <LoadingDots />
                    </Paper>
                  </Box>
                )}
                <div ref={messagesEndRef} style={{ height: '1px', marginTop: '4px' }} />
              </Box>
              <MessageInput 
                onSendMessage={handleSendMessage}
                loading={loading}
                inputRef={messageInputRef}
                bot={bot}
                selectedTask={selectedTask}
              />
            </>
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
                p: { xs: 2, sm: 2, md: 3 },
                textAlign: "center",
                mt: { xs: 2, sm: 3 },
                overflowX: "auto",
                '&::-webkit-scrollbar': {
                  height: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  background: 'transparent',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: `${bot.botColor}80`,
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb:hover': {
                  background: bot.botColor,
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  mb: 4,
                  alignItems: "flex-start",
                  maxWidth: "85%",
                }}
              >
                <Avatar
                  src={characterImages[bot.botId]}
                  sx={{ 
                    width: { xs: 48, sm: 52, md: 56 },
                    height: { xs: 48, sm: 52, md: 56 },
                    mr: 2,
                    border: `1px solid ${bot.botColor}40`,
                  }}
                />
                <Box>
                  <Paper
                    sx={{
                      p: { xs: 2.5, sm: 3, md: 3 },
                      bgcolor: `${bot.botColor}10`,
                      borderRadius: 2.5,
                      border: `1px solid ${bot.botColor}30`,
                      textAlign: 'left'
                    }}
                  >
                    <Typography sx={{ 
                      mb: 2,
                      fontSize: { xs: '1.1rem', sm: '1.2rem', md: '1.25rem' },
                      fontWeight: 500,
                    }}>
                      How can I assist you?
                    </Typography>
                    <Typography 
                      variant="body2" 
                      color="text.secondary"
                      sx={{
                        fontSize: { xs: '0.9rem', sm: '0.95rem', md: '1rem' },
                      }}
                    >
                      Here are some tasks I can help you with:
                    </Typography>
                  </Paper>
                </Box>
              </Box>

              <Box sx={{ 
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                gap: 2,
                width: '100%',
                pl: { xs: "calc(48px + 2rem)", sm: "calc(52px + 2rem)", md: "calc(56px + 2rem)" },
                pr: { xs: 2, sm: 2, md: 3 },
                minWidth: 'min-content',
              }}>
                {bot.tasks.map((task, index) => (
                  <Card
                    key={index}
                    sx={{
                      backgroundColor: `${bot.botColor}10`,
                      borderRadius: 2,
                      border: `1px solid ${bot.botColor}30`,
                      transition: 'all 0.2s ease-in-out',
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'flex-start',
                      minHeight: 'unset',
                      p: 2,
                      gap: 2,
                      '&:hover': {
                        backgroundColor: `${bot.botColor}15`,
                        transform: 'translateY(-1px)',
                      }
                    }}
                    onClick={() => handleTaskSelect(task)}
                  >
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: 1.5,
                        backgroundColor: `${bot.botColor}20`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <i 
                        className={task.taskIcon}
                        style={{ 
                          color: bot.botColor, 
                          fontSize: '1rem',
                        }} 
                      />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography 
                        variant="subtitle1" 
                        sx={{ 
                          fontWeight: 600,
                          color: 'text.primary',
                          fontSize: '0.95rem',
                          mb: 0.5,
                        }}
                      >
                        {task.taskTitle}
                      </Typography>
                      <Typography 
                        variant="body2"
                        sx={{
                          color: 'text.secondary',
                          fontSize: '0.85rem',
                          lineHeight: 1.4,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      >
                        {task.taskDescription}
                      </Typography>
                    </Box>
                  </Card>
                ))}
              </Box>
            </Box>
          )}
        </Paper>
      </Box>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => !isDeleting && setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Chat</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this chat? This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={isDeleting}>
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteThread} 
            color="error" 
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={20} /> : null}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <AddToProjectModal
        open={showAddToProjectModal}
        onClose={() => setShowAddToProjectModal(false)}
        contentType={selectedContent?.contentType}
        contentValue={selectedContent?.contentValue}
        botTaskName={selectedTask?.name}
      />
    </Box>
  );
};

export default BotChat; 